<template>
  <div
    class="rounded-xl overflow-hidden relative flex flex-col shadow-md min-w-[260px] bg-white"
  >
    <div
      class="absolute top-0 left-0 w-full h-full border opacity-20 rounded-xl pointer-events-none"
      :style="{ borderColor: category.color }"
    />
    <div
      :style="{ backgroundColor: category.color }"
      class="text-base px-6 py-2 text-white flex justify-between items-center gap-2 min-h-[3.5rem]"
    >
      <div
        class="font-bold"
        :style="{
          color: category.titleColor,
          ...useEditorFontFamily(category.titleFontFamily),
        }"
      >
        {{ category.title.toUpperCase() }}
      </div>
      <BaseButton
        v-if="category.additionalInfo"
        look="none"
        class="!p-0"
        @click="isAboutModalOpen = true"
      >
        <BaseIcon name="outline_question_mark_circle" class="opacity-50" />
      </BaseButton>
    </div>
    <div class="px-6 py-4 flex flex-col gap-4 text-sm h-full">
      <div
        class="flex flex-col gap-4"
        :style="{
          color: category.textColor,
          ...useEditorFontFamily(category.textFontFamily),
        }"
      >
        <div
          v-if="category.text"
          class="marked"
          v-html="getMarkedTextWithBlankLinks(category.text)"
        />
      </div>
      <div
        v-if="category.tickets?.length"
        class="flex flex-col gap-4 flex-1 justify-end pt-4"
      >
        <template v-if="category.tickets?.length > 1 && currentTicketId">
          <BaseRadioGroup
            v-model="currentTicketId"
            :list-item-class="'text-[15px] font-bold'"
            :options="category.tickets"
          />
        </template>
        <div class="flex flex-col min-h-[4.5rem] items-center">
          <div
            class="flex text-2xl font-bold gap-1 justify-center items-center flex-wrap"
          >
            <template
              v-if="(rawPrice && price) || !currentTicketConfig?.freeTicketText"
            >
              <div>{{ price }}</div>
              <div v-if="futurePrice" class="text-gray-200 line-through">
                {{ futurePrice }}
              </div>
            </template>
            <template v-else>
              {{ currentTicketConfig?.freeTicketText }}
            </template>
          </div>
          <div
            v-if="category.isShowVat"
            class="text-2xl font-bold text-gray-200 text-center italic"
          >
            {{ $t('tickets.vat') }}
          </div>
          <BaseButton
            v-if="category.additionalInfo"
            look="link"
            class="!font-normal"
            size="sm"
            @click="isAboutModalOpen = true"
          >
            {{
              category.aboutTicketText ||
              $t('tickets.about_ticket').toLowerCase()
            }}
          </BaseButton>
        </div>
        <div class="flex items-center gap-4 justify-center">
          <template v-if="rawPrice || isInIframe">
            <div
              v-if="maxTicketsCount > 1"
              class="border flex items-center gap-1 rounded-xl h-10 px-3 border-primary-800"
            >
              <BaseButton
                look="none"
                class="!p-1"
                :class="
                  !currentTicketSelectCount &&
                  currentTicketCount === 1 &&
                  '!opacity-50'
                "
                :disabled="
                  !currentTicketSelectCount && currentTicketCount === 1
                "
                @click="removeTicket"
              >
                -
              </BaseButton>
              <div class="w-5 text-center">{{ currentTicketCount }}</div>
              <BaseButton
                look="none"
                class="!p-1"
                :disabled="currentTicketCount === maxTicketsCount"
                @click="addTicket"
              >
                +
              </BaseButton>
            </div>
            <BaseLink
              v-if="currentTicketSelectCount > 0"
              :to="isInIframe ? undefined : ordersStore.cartRoute"
              :href="ordersStore.cartLink"
              target="_blank"
              theme="none"
              @click="goToCart"
            >
              <BaseButton>
                {{ $t('tickets.go_to_cart') }}
              </BaseButton>
            </BaseLink>
            <BaseButton
              v-else
              look="border"
              class="!h-10 hover:bg-primary-800 hover:text-white active:text-white"
              @click="addToCart"
            >
              {{ $t('tickets.add_to_cart') }}
            </BaseButton>
          </template>
          <div v-else class="flex flex-col gap-2">
            <div
              v-if="eventsStore.event?.is_has_ticket"
              class="text-green-500 text-center"
            >
              {{ $t('tickets.already_registered') }}
            </div>
            <BaseButton
              look="border"
              class="!h-10 hover:bg-primary-800 hover:text-white active:text-white"
              :loading="freeTicketRegistrationId === currentTicketId"
              :disabled="
                !!(eventsStore.event?.is_has_ticket || freeTicketRegistrationId)
              "
              @click="emits('registerFreeTicket', currentTicketId)"
            >
              {{ $t('tickets.register') }}
            </BaseButton>
          </div>
        </div>
      </div>
      <div
        v-else-if="category.isSubscriptionSection"
        class="flex flex-col gap-4 flex-1 justify-end pt-4 items-center"
      >
        <BaseButton
          v-if="category.additionalInfo"
          look="link"
          class="!font-normal"
          size="sm"
          @click="isAboutModalOpen = true"
        >
          {{
            category.aboutTicketText || $t('tickets.about_ticket').toLowerCase()
          }}
        </BaseButton>
        <EditorButton
          v-if="authStore.isProActive"
          :button="category.subscriptionActiveButton?.[0]"
        />
        <EditorButton v-else :button="category.subscribeButton?.[0]" />
      </div>
    </div>
    <TicketsCategoryCardModal
      v-if="category.additionalInfo"
      :text="category.additionalInfo"
      :ticket-id="currentTicketId"
      :is-open="isAboutModalOpen"
      :popup-title="category.popupTitle"
      :ticket-count="currentTicketCount"
      @close="isAboutModalOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import TicketsCategoryCardModal from './TicketsCategoryCardModal.vue'
import { Currency, type TicketDetails } from '~/models/orders'
import { useOrders } from '~/stores/orders'
import EditorButton from '~/components/page-editor/EditorButton.vue'
import { languageMap } from '~/models/locale'
import { useAuth } from '~/stores/auth'

export interface TicketSchema {
  label: string
  value: number
  maxTicketsCount?: number
  freeTicketText?: string
}

export interface Category {
  title: string
  text?: string
  color?: string
  textColor?: string
  titleColor?: string
  textFontFamily?: string
  titleFontFamily?: string
  isShowVat?: boolean
  aboutTicketText?: string
  popupTitle?: string
  subscribeButton?: any
  subscriptionActiveButton?: any
  isSubscriptionSection?: boolean
  additionalInfo?: string
  tickets: TicketSchema[]
}

interface PropsInterface {
  category: Category
  ticketsInfo: TicketDetails[]
  isInIframe: boolean
  freeTicketRegistrationId: TicketDetails['id'] | null
}

const props = withDefaults(defineProps<PropsInterface>(), {
  category: () => ({
    title: '',
    text: '',
    color: '#0D65D8',
    tickets: [],
    isShowVat: true,
  }),
  ticketsInfo: () => [],
  isInIframe: false,
  freeTicketRegistrationId: null,
})

const emits = defineEmits(['update', 'registerFreeTicket', 'goToCart'])
const eventsStore = useEvents()
const authStore = useAuth()
const ordersStore = useOrders()
const { getMarkedTextWithBlankLinks } = useMarkedText()
const i18n = useI18n()
const lang = i18n.locale.value

const isAboutModalOpen = ref(false)
const MAX_TICKET_COUNT = 99
const ticketCountToAdd = ref<number>(1)
const currentTicketId = ref<number | null>(null)
const { selectedTickets } = storeToRefs(ordersStore)

watchEffect(() => {
  // Update currentTicketId when props.category.tickets change
  currentTicketId.value = props.category.tickets[0]?.value || null
})

const currentTicketInfo = computed(() => {
  return props.ticketsInfo.find((ticket) => ticket.id === currentTicketId.value)
})

const currentTicketConfig = computed(() => {
  return props.category.tickets.find(
    (ticket) => ticket.value === currentTicketId.value
  )
})

const maxTicketsCount = computed(() => {
  return currentTicketConfig.value?.maxTicketsCount || MAX_TICKET_COUNT
})

const currency = computed(() => {
  return eventsStore.currency
})

const rawPrice = computed(() => {
  return currentTicketInfo.value
    ? currentTicketInfo.value[
        currency.value.toLowerCase() as Lowercase<Currency>
      ] || 0
    : 0
})

const locale = computed(() => {
  return currency.value === Currency.RUB ? languageMap.ru : languageMap[lang]
})

const price = computed(() => {
  return currentTicketInfo.value
    ? ordersStore.formatPrice(
        currentTicketInfo.value[
          currency.value.toLowerCase() as Lowercase<Currency>
        ] || 0,
        currency.value,
        locale.value
      )
    : ''
})

const futurePrice = computed(() => {
  return currentTicketInfo.value?.future_price
    ? ordersStore.formatPrice(
        currentTicketInfo.value.future_price,
        currency.value,
        locale.value
      )
    : ''
})

const currentTicketSelectCount = computed(
  () =>
    (currentTicketId.value && selectedTickets.value[currentTicketId.value]) || 0
)

const currentTicketCount = computed(() => {
  return currentTicketSelectCount.value || ticketCountToAdd.value
})

const addTicket = () => {
  if (currentTicketSelectCount.value >= maxTicketsCount.value) {
    return
  }

  if (currentTicketSelectCount.value) {
    emits('update', currentTicketId.value, currentTicketSelectCount.value + 1)
  } else {
    ticketCountToAdd.value += 1
  }
}

const removeTicket = () => {
  if (currentTicketSelectCount.value) {
    // user could have increased the counter of another ticket in this card,
    if (currentTicketSelectCount.value === 1) {
      ticketCountToAdd.value = 1
    }

    emits('update', currentTicketId.value, currentTicketSelectCount.value - 1)
  } else {
    ticketCountToAdd.value = Math.max(1, ticketCountToAdd.value - 1)
  }
}

const addToCart = () => {
  emits('update', currentTicketId.value, ticketCountToAdd.value)
  ticketCountToAdd.value = 1
}

const goToCart = () => {
  emits('goToCart')
}
</script>
